/**
 * CategoriesPage
 *
 * Enhanced category management with:
 * - Polished visual design
 * - Category detail modal with transaction history
 * - Inline rename functionality
 * - Budget progress indicators
 * - Expense/Income tabs with smooth transitions
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { db, Category, Transaction, Account, Budget } from '@services/db';
import { categoryService } from '@services/categories';
import { dataEvents } from '@services/dataEvents';
import { CategoryDetailModal, CategoryWithStats } from '@/components/categories';
import { AddCategoryModal } from '@/components/AddCategoryModal';
import { Button } from '@/components/shared';
import { formatCurrency as formatCurrencyIntl, formatMonthYear } from '@/utils/format';
import { getCategoryIcon } from '@/utils/categoryIcons';
import { useLanguage, categoryDisplayName } from '@/i18n';
import './CategoriesPage.css';

// ============================================================
// Helpers
// ============================================================

// Central locale-aware formatters (utils/format.ts) - never inline Intl here
function formatCurrency(amount: number): string {
  return formatCurrencyIntl(amount, 'USD', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function formatMonth(date: Date): string {
  return formatMonthYear(date);
}

// Icons: shared resolver in utils/categoryIcons.ts (exact + keyword match)

// Progress bar colors based on percentage
function getProgressColor(percentage: number): string {
  if (percentage >= 100) return 'var(--expense)';
  if (percentage >= 80) return '#f0ad4e';
  return 'var(--income)';
}

// ============================================================
// Types (CategoryWithStats imported from shared component)
// ============================================================

// ============================================================
// Main Component
// ============================================================

export default function CategoriesPage() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'expense' | 'income'>('expense');
  // Normalized to the 1st: month arithmetic on day 29-31 skips/repeats
  // months (e.g. Jul 31 - 1 month = Jun 31 -> rolls back to Jul 1)
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [categories, setCategories] = useState<Category[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<CategoryWithStats | null>(null);
  const [showAddCategory, setShowAddCategory] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [cats, txns, accs, budg] = await Promise.all([
        db.categories.toArray(),
        db.transactions.toArray(),
        db.accounts.toArray(),
        db.budgets.toArray(),
      ]);
      setCategories(cats);
      setTransactions(txns);
      setAccounts(accs);
      setBudgets(budg);
    } catch (err) {
      console.error('[CategoriesPage] Load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Subscribe to data events for real-time updates
  useEffect(() => {
    const unsubCategories = dataEvents.on('categories', loadData);
    const unsubTransactions = dataEvents.on('transactions', loadData);
    const unsubAccounts = dataEvents.on('accounts', loadData);
    return () => {
      unsubCategories();
      unsubTransactions();
      unsubAccounts();
    };
  }, [loadData]);

  // The global FAB / sidebar quick-add button opens Add Category while this
  // page is active (App.tsx dispatches the event; the modal lives here so
  // it inherits the active expense/income tab)
  useEffect(() => {
    const open = () => setShowAddCategory(true);
    window.addEventListener('njc:add-category', open);
    return () => window.removeEventListener('njc:add-category', open);
  }, []);

  // Navigate months
  const goToPrevMonth = useCallback(() => {
    setCurrentMonth((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() - 1);
      return d;
    });
  }, []);

  const goToNextMonth = useCallback(() => {
    setCurrentMonth((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + 1);
      return d;
    });
  }, []);

  // Handle rename
  const handleRenameCategory = useCallback(async (id: string, newName: string) => {
    const result = await categoryService.update(id, { name: newName });
    if (!result.success) {
      console.error('[CategoriesPage] Failed to rename category:', result.error);
      return;
    }
    console.log('[CategoriesPage] Renamed category:', id, 'to', newName);
    loadData();

    // Update selected category if it's the one being renamed
    if (selectedCategory?.id === id) {
      setSelectedCategory((prev) => prev ? { ...prev, name: newName } : null);
    }
  }, [loadData, selectedCategory]);

  // Handle month change in modal
  const handleModalMonthChange = useCallback((direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + (direction === 'next' ? 1 : -1));
      return d;
    });
  }, []);

  // Handle delete (service refuses while transactions/schedules still use
  // the category; the modal renders that result inline)
  const handleDeleteCategory = useCallback(async (id: string) => {
    const result = await categoryService.delete(id);
    if (result.success) {
      setSelectedCategory(null);
      loadData();
    }
    return result;
  }, [loadData]);

  // Filter and calculate stats
  const categoryStats = useMemo((): CategoryWithStats[] => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // Filter categories by type
    const filteredCategories = categories.filter((c) => c.type === activeTab);

    // Filter transactions by month and type
    const monthTxns = transactions.filter((t) => {
      const d = new Date(t.date);
      return (
        d.getFullYear() === year &&
        d.getMonth() === month &&
        t.type === activeTab
      );
    });

    // Calculate stats per category
    const stats: CategoryWithStats[] = filteredCategories.map((cat) => {
      const catTxns = monthTxns.filter((t) => t.categoryId === cat.id);
      const amount = catTxns.reduce((sum, t) => sum + t.amount, 0);

      // Real budgets only (previously a hardcoded mock table showed fake
      // budget bars). Monthly active budgets come from imports/backups;
      // there is no in-app budget editor yet, so most users have none.
      const budgetRecord = budgets.find(
        (b) => b.categoryId === cat.id && b.isActive && b.period === 'monthly'
      );
      const budget = budgetRecord?.amount;
      const percentage = budget ? Math.round((amount / budget) * 100) : undefined;

      return {
        ...cat,
        amount,
        transactionCount: catTxns.length,
        budget,
        percentage,
      };
    });

    // Sort by amount (highest first), then alphabetically
    return stats.sort((a, b) => {
      if (b.amount !== a.amount) return b.amount - a.amount;
      return a.name.localeCompare(b.name);
    });
  }, [categories, transactions, budgets, currentMonth, activeTab]);

  // Calculate total
  const total = useMemo(() => {
    return categoryStats.reduce((sum, cat) => sum + cat.amount, 0);
  }, [categoryStats]);

  // Categories with transactions vs empty
  const activeCategories = categoryStats.filter((c) => c.transactionCount > 0);
  const emptyCategories = categoryStats.filter((c) => c.transactionCount === 0);

  return (
    <div className="categories-page animate-fade">
      {/* Page Header */}
      <div className="categories-header">
        <h1 className="page-title">{t('nav.categories')}</h1>
        {/* Was a dead button - no handler at all; now opens the add modal */}
        <button
          className="header-action-btn"
          onClick={() => setShowAddCategory(true)}
          aria-label={t('categories.add_aria')}
          title={t('categories.add_aria')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      </div>

      {/* Tabs */}
      <div className="category-tabs">
        <button
          className={`category-tab ${activeTab === 'expense' ? 'active expense' : ''}`}
          onClick={() => setActiveTab('expense')}
        >
          <span className="tab-icon">📤</span>
          <span className="tab-label">{t('common.expenses')}</span>
        </button>
        <button
          className={`category-tab ${activeTab === 'income' ? 'active income' : ''}`}
          onClick={() => setActiveTab('income')}
        >
          <span className="tab-icon">📥</span>
          <span className="tab-label">{t('common.income')}</span>
        </button>
      </div>

      {/* Month Selector */}
      <div className="month-selector">
        <button className="month-nav" onClick={goToPrevMonth} aria-label={t('transactions.aria.prev_month')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <span className="month-label">{formatMonth(currentMonth)}</span>
        <button className="month-nav" onClick={goToNextMonth} aria-label={t('transactions.aria.next_month')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {/* Total Summary Card */}
      {!loading && categoryStats.length > 0 && (
        <div className={`total-summary-card ${activeTab}`}>
          <div className="total-label">{activeTab === 'expense' ? t('categories.total_expenses') : t('categories.total_income')}</div>
          <div className="total-amount">
            {activeTab === 'expense' ? '-' : '+'}{formatCurrency(total)}
          </div>
          <div className="total-meta">
            {t('categories.active_count', { count: activeCategories.length })}
          </div>
        </div>
      )}

      {/* Category List */}
      <div className="categories-list">
        {loading ? (
          <div className="categories-loading">
            <div className="loading-spinner" />
            <span>{t('categories.loading')}</span>
          </div>
        ) : categoryStats.length === 0 ? (
          <div className="categories-empty">
            <span className="empty-icon">{activeTab === 'expense' ? '📤' : '📥'}</span>
            <p className="empty-title">{activeTab === 'expense' ? t('categories.empty_title_expense') : t('categories.empty_title_income')}</p>
            <p className="empty-text">{t('categories.empty_text')}</p>
            <Button variant="primary" onClick={() => setShowAddCategory(true)}>
              + {t('categories.add.title')}
            </Button>
          </div>
        ) : (
          <>
            {/* Active Categories */}
            {activeCategories.length > 0 && (
              <div className="categories-section">
                <div className="categories-grid">
                  {activeCategories.map((cat) => (
                    <button
                      key={cat.id}
                      className={`category-card ${activeTab}`}
                      onClick={() => setSelectedCategory(cat)}
                    >
                      <div
                        className="category-card-icon"
                        style={{ background: cat.color ? `${cat.color}25` : 'var(--bg-surface)' }}
                      >
                        <span>{getCategoryIcon(cat.name, cat.icon)}</span>
                      </div>
                      <div className="category-card-content">
                        <div className="category-card-name">{categoryDisplayName(cat.name)}</div>
                        <div className="category-card-count">
                          {t(
                            cat.transactionCount === 1 ? 'categories.txn_count_one' : 'categories.txn_count_other',
                            { count: cat.transactionCount }
                          )}
                          {total > 0 && cat.amount > 0 && (
                            <span className="category-card-share">
                              {' · '}{Math.round((cat.amount / total) * 100)}%
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="category-card-amount">
                        {formatCurrency(cat.amount)}
                      </div>
                      {cat.percentage !== undefined && (
                        <div className="category-card-progress">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${Math.min(100, cat.percentage)}%`,
                              background: getProgressColor(cat.percentage),
                            }}
                          />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Empty Categories */}
            {emptyCategories.length > 0 && (
              <div className="categories-section">
                <div className="section-header">
                  <span className="section-title">{t('categories.no_activity')}</span>
                  <span className="section-count">{emptyCategories.length}</span>
                </div>
                <div className="empty-categories-grid">
                  {emptyCategories.map((cat) => (
                    <button
                      key={cat.id}
                      className="category-chip"
                      onClick={() => setSelectedCategory(cat)}
                    >
                      <span className="chip-icon">{getCategoryIcon(cat.name, cat.icon)}</span>
                      <span className="chip-name">{categoryDisplayName(cat.name)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* In-flow add affordance - the header "+" proved invisible
                (owner never noticed it); a labeled chip in the list is the
                obvious path, alongside the context-aware FAB */}
            <button
              className="category-chip category-chip-add"
              onClick={() => setShowAddCategory(true)}
            >
              <span className="chip-icon">+</span>
              <span className="chip-name">{t('categories.add.title')}</span>
            </button>
          </>
        )}
      </div>

      {/* Category Detail Modal */}
      <CategoryDetailModal
        category={selectedCategory}
        transactions={transactions}
        accounts={accounts}
        currentMonth={currentMonth}
        onClose={() => setSelectedCategory(null)}
        onRename={handleRenameCategory}
        onDelete={handleDeleteCategory}
        onMonthChange={handleModalMonthChange}
      />

      {/* Add Category Modal (type preset from the active tab) */}
      <AddCategoryModal
        isOpen={showAddCategory}
        initialType={activeTab}
        onClose={() => setShowAddCategory(false)}
        onSuccess={loadData}
      />
    </div>
  );
}
